import React, { useEffect, useState } from "react";
import { Database, Play, Plus, Trash2, Menu, X } from "lucide-react";
import { NotebookSidebar, Notebook } from "./NoteBookListSidebar";
import { api, CreateCellPayload } from "../common/api";
import { toast } from "react-toastify";

interface NotebookListProps {
  userRole?: string;
  onOpenNotebook?: (notebookId: string) => void;
}

interface Cell {
  id: number;
  query: string;
  output: QueryResult | null;
  executionCount: number | null;
}

interface QueryResult {
  success: boolean;
  columns?: Array<{
    name: string;
    type: number;
    database_type: string;
    table: string;
    column_id: number;
  }>;
  rows?: any[];
  rowCount?: number;
  message?: string;
  error?: string;
  executionTime: string;
}

interface SQLCellProps {
  cell: Cell;
  onUpdate: (id: number, query: string) => void;
  onDelete: (id: number) => void;
  onExecute: (id: number, result: QueryResult) => void;
}

interface CreateNotebookModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreate: (name: string, description: string) => void;
}

const token = JSON.parse(localStorage.getItem("token") || "null");
const accessToken = token?.accessToken;

// Create Notebook Modal Component
const CreateNotebookModal: React.FC<CreateNotebookModalProps> = ({
  isOpen,
  onClose,
  onCreate,
}) => {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // if (name.trim()) {
    //   onCreate(name.trim(), description.trim());
    //   setName("");
    //   setDescription("");
    //   onClose();
    // }

    const connectionDetails = JSON.parse(
      localStorage.getItem("activeConnection") || "{}"
    );
    const connectionId = connectionDetails.connection_id;

    const payload = {
      name: name.trim(),
      description: description.trim(),
      connectionId: connectionId,
    };

    try {
      const response = await api.createNotebook(payload);
      localStorage.setItem("activeNotebook", JSON.stringify(response));
      setName("");
      setDescription("");
      onClose();
      if (response.ok) {
        toast.success("Notebook created successfully");
      }
    } catch (error) {
      toast.error("Failed to create notebook");
    }
  };

  const handleClose = () => {
    setName("");
    setDescription("");
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-lg shadow-xl max-w-md w-full border border-gray-700">
        {/* Modal Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-700">
          <h2 className="text-xl font-semibold text-gray-100">
            Create New Notebook
          </h2>
          <button
            onClick={handleClose}
            className="p-1 hover:bg-gray-700 rounded transition-colors"
          >
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        {/* Modal Body */}
        <form onSubmit={handleSubmit}>
          <div className="px-6 py-4 space-y-4">
            {/* Name Input */}
            <div>
              <label
                htmlFor="notebook-name"
                className="block text-sm font-medium text-gray-300 mb-2"
              >
                Notebook Name <span className="text-red-400">*</span>
              </label>
              <input
                id="notebook-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., User Analysis, Sales Report"
                className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-gray-100 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                autoFocus
                required
              />
            </div>
            <div>
              <label
                htmlFor="notebook-description"
                className="block text-sm font-medium text-gray-300 mb-2"
              >
                Description <span className="text-gray-500">(Optional)</span>
              </label>
              <textarea
                id="notebook-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Brief description of what this notebook is for..."
                className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-gray-100 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                rows={3}
              />
            </div>
          </div>

          {/* Modal Footer */}
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-700">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 text-gray-300 hover:bg-gray-700 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!name.trim()}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Create Notebook
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
// SQL Cell Component
const SQLCell: React.FC<SQLCellProps> = ({
  cell,
  onUpdate,
  onDelete,
  onExecute,
}) => {
  const [isExecuting, setIsExecuting] = useState(false);

  const handleExecute = async () => {
    setIsExecuting(true);
    await onExecute(cell.id, {} as QueryResult);
    setIsExecuting(false);
  };

  const handleQueryChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    onUpdate(cell.id, e.target.value);
  };

  return (
    <div className="border border-gray-700 rounded-lg mb-4 bg-gray-800 shadow-sm">
      {/* Cell Header */}
      <div className="flex items-center justify-between px-4 py-2 bg-gray-900 border-b border-gray-700">
        <div className="flex items-center gap-2">
          <span className="text-xs font-mono text-gray-400">
            Cell [{cell.executionCount || "-"}]
          </span>
          {cell.output && (
            <span className="text-xs text-gray-500">
              {cell.output.executionTime}
            </span>
          )}
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleExecute}
            disabled={isExecuting}
            className="p-1.5 hover:bg-gray-700 rounded transition-colors disabled:opacity-50 text-gray-300"
            title="Run cell (Ctrl+Enter)"
          >
            <Play className={`w-4 h-4 ${isExecuting ? "animate-pulse" : ""}`} />
          </button>
          <button
            onClick={() => onDelete(cell.id)}
            className="p-1.5 hover:bg-red-900 rounded transition-colors text-red-400"
            title="Delete cell"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* SQL Input */}
      <div className="relative">
        <textarea
          value={cell.query}
          onChange={handleQueryChange}
          placeholder="-- Write your SQL query here
SELECT * FROM users LIMIT 10;"
          className="w-full p-4 font-mono text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-900 text-gray-100 placeholder-gray-500"
          rows={6}
          spellCheck={false}
        />
      </div>

      {/* Output */}
      {cell.output && (
        <div className="border-t border-gray-700">
          {cell.output.success ? (
            <div className="p-4">
              {cell.output.rows ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-700 text-sm">
                    <thead className="bg-gray-900">
                      <tr>
                        {cell.output?.columns?.map((col, idx) => (
                          <th
                            key={idx}
                            className="px-4 py-2 text-left text-xs font-medium text-gray-300 uppercase tracking-wider"
                          >
                            {col.name}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="bg-gray-800 divide-y divide-gray-700">
                      {cell.output.rows.map((row, rowIdx) => (
                        <tr key={rowIdx} className="hover:bg-gray-700">
                          {cell.output?.columns?.map((col, cellIdx) => (
                            <td
                              key={cellIdx}
                              className="px-4 py-2 whitespace-nowrap text-gray-200"
                            >
                              {row[col.name]}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div className="mt-2 text-xs text-gray-400">
                    {cell.output.rowCount} rows returned
                  </div>
                </div>
              ) : (
                <div className="text-sm text-green-400 bg-green-900 bg-opacity-30 p-3 rounded">
                  ✓ {cell.output.message}
                </div>
              )}
            </div>
          ) : (
            <div className="p-4">
              <div className="text-sm text-red-400 bg-red-900 bg-opacity-30 p-3 rounded font-mono">
                ✗ Error: {cell.output.error}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export const NotebookList: React.FC<NotebookListProps> = () => {
  const [cells, setCells] = useState<Cell[]>([]);
  const [nextId, setNextId] = useState(2);
  const [executionCounter, setExecutionCounter] = useState(1);

  const [notebooks, setNotebooks] = useState<Notebook[]>([]);
  const [currentNotebook, setCurrentNotebook] = useState<Notebook | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [nextNotebookId, setNextNotebookId] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    const fetchNotebooks = async () => {
      try {
        const token = JSON.parse(localStorage.getItem("token") || "null");
        const accessToken = token?.accessToken;

        api.setAuthToken(accessToken);
        const response = await api.getNotebook();
        setNotebooks(response.notebooks || []);
        if (response.notebooks && response.notebooks.length > 0) {
          setCurrentNotebook(response.notebooks[0]);
        }
      } catch (error) {
        // toast.error("Failed to fetch notebooks");
        console.error("Failed to fetch notebooks:", error);
      }
    };
    fetchNotebooks();
  }, []);

  useEffect(() => {
    const getAllCellsRelatedToNotebook = async () => {
      try {
        api.setAuthToken(accessToken);
        if (!currentNotebook?.notebook_id) return;
        const response = await api.getAllCellsRelatedToNotebook(
          currentNotebook.notebook_id
        );
        const fetchedCells: Cell[] = response.cells.map((cellData: any) => ({
          id: cellData.cell_index,
          query: cellData.sql,
          output: null,
          executionCount: null,
        }));
        setCells(fetchedCells);
      } catch (error) {
        console.error("Failed to fetch cells for notebook:", error);
      }
    };
    getAllCellsRelatedToNotebook();
  }, [currentNotebook]);

  const addCell = () => {
    setCells([
      ...cells,
      {
        id: nextId,
        query: "",
        output: null,
        executionCount: null,
      },
    ]);
    setNextId(nextId + 1);
  };

  const deleteCell = async (id: number) => {
    try {
      api.setAuthToken(accessToken);
      if (!currentNotebook?.notebook_id) return;
      await api.deleteCellCurrentNotebook(currentNotebook.notebook_id, id);
      setCells(cells.filter((cell) => cell.id !== id));
    } catch (error) {}
  };

  const updateCell = (id: number, query: string) => {
    setCells(cells.map((cell) => (cell.id === id ? { ...cell, query } : cell)));
  };

  const executeCell = async (id: number, result: QueryResult) => {
    const connectionDetails = JSON.parse(
      localStorage.getItem("activeConnection") || "{}"
    );
    const connectionId = connectionDetails.connection_id;

    if (!connectionId) {
      toast.error("No active connection found");
      return;
    }

    console.log("Executing cell for connection ID:", connectionId);

    try {
      const cellQuery = cells.find((cell) => cell.id === id)?.query || "";

      if (!cellQuery.trim()) {
        toast.error("Cannot execute empty query");
        return;
      }
      if (!currentNotebook?.notebook_id) {
        toast.error("No active notebook found");
        return;
      }

      const createCellPayload: CreateCellPayload = {
        sql: cellQuery,
        connectionId: connectionId,
        cell_index: id,
      };
      api.setAuthToken(accessToken);
      const cellResponse = await api.createCell(
        createCellPayload,
        currentNotebook?.notebook_id
      );
      console.log("Cell created:", cellResponse);
      const executePayload = {
        connection_id: connectionId,
        sql: cellQuery,
      };
      api.setAuthToken(accessToken);
      const executeResponse = await api.executeQuery(executePayload);
      console.log("Query executed:", executeResponse);

      const queryResult: QueryResult = {
        success: true,
        columns: executeResponse.columns || [],
        rows: executeResponse.rows || [],
        rowCount: executeResponse.rows?.length || 0,
        executionTime: executeResponse.executionTime || "0.00s",
        message: executeResponse.message,
      };

      const updateCellPayload = {
        notebook_id: currentNotebook?.notebook_id,
        cell_index: id,
        sql: cellQuery,
        output: queryResult,
      };
      await api.updateCell(updateCellPayload);
      console.log("Cell updated successfully");

      setCells(
        cells.map((cell) =>
          cell.id === id
            ? {
                ...cell,
                output: queryResult,
                executionCount: executionCounter,
              }
            : cell
        )
      );
      setExecutionCounter(executionCounter + 1);

      toast.success("Cell executed successfully");
    } catch (error: any) {
      console.error("Error executing cell:", error);

      const errorResult: QueryResult = {
        success: false,
        error:
          error?.message || error?.data?.message || "Failed to execute query",
        executionTime: "0.00s",
      };

      setCells(
        cells.map((cell) =>
          cell.id === id
            ? {
                ...cell,
                output: errorResult,
                executionCount: executionCounter,
              }
            : cell
        )
      );
      setExecutionCounter(executionCounter + 1);

      toast.error(error?.message || "Failed to execute cell");
    }
  };
  const openCreateModal = () => {
    setIsModalOpen(true);
  };

  const createNewNotebook = (name: string, description: string) => {
    const newNotebook: Notebook = {
      name: name,
      description: description,
      cell_count: 1,
    };
    setNotebooks([...notebooks, newNotebook]);
    setNextNotebookId(nextNotebookId + 1);
    setCurrentNotebook(newNotebook);

    setNextId(2);
    setExecutionCounter(1);
  };

  const deleteNotebook = (notebookId: string) => {
    try {
      api.setAuthToken(accessToken);
      api.deleteNotebook(notebookId);
      const updatedNotebooks = notebooks.filter(
        (notebook) => notebook.notebook_id !== notebookId
      );
      setNotebooks(updatedNotebooks);
      if (
        updatedNotebooks.length > 0 &&
        currentNotebook?.notebook_id === notebookId
      ) {
        setCurrentNotebook(updatedNotebooks[0]);
      } else if (updatedNotebooks.length === 0) {
        setCurrentNotebook(null);
      }
      toast.success("Notebook deleted successfully");
    } catch (error) {
      toast.error("Failed to delete notebook");
    }
  };

  const switchNotebook = (notebook: Notebook) => {
    setCurrentNotebook(notebook);
  };

  const exportNotebook = () => {
    const notebookData = {
      cells: cells.map((cell) => ({
        query: cell.query,
        output: cell.output,
      })),
      timestamp: new Date().toISOString(),
    };

    const blob = new Blob([JSON.stringify(notebookData, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `sql-notebook-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // if (notebooks.length === 0) {
  //   return (
  //     <div className="min-h-screen bg-gray-900 flex items-center justify-center">
  //       <CreateNotebookModal
  //         isOpen={isModalOpen}
  //         onClose={() => setIsModalOpen(false)}
  //         onCreate={createNewNotebook}
  //       />
  //       <div className="text-center max-w-md px-6">
  //         <Database className="w-16 h-16 text-blue-500 mx-auto mb-4" />
  //         <h2 className="text-2xl font-bold text-gray-100 mb-2">
  //           No Notebooks Yet
  //         </h2>
  //         <p className="text-gray-400 mb-6">
  //           Create your first notebook to start writing and executing SQL
  //           queries
  //         </p>
  //         <button
  //           onClick={openCreateModal}
  //           className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2 mx-auto"
  //         >
  //           <Plus className="w-5 h-5" />
  //           Create Your First Notebook
  //         </button>
  //       </div>
  //     </div>
  //   );
  // }

  return (
    <div className="min-h-screen bg-gray-900 flex">
      {/* Create Notebook Modal */}
      <CreateNotebookModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onCreate={createNewNotebook}
      />

      {/* Sidebar */}
      <NotebookSidebar
        notebooks={notebooks}
        currentNotebook={currentNotebook}
        sidebarOpen={sidebarOpen}
        onCreateNotebook={openCreateModal}
        onSwitchNotebook={switchNotebook}
        onDeleteNotebook={deleteNotebook}
      />

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <div className="bg-gray-800 border-b border-gray-700 shadow-sm flex-shrink-0">
          <div className="max-w-6xl mx-auto px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setSidebarOpen(!sidebarOpen)}
                  className="p-2 hover:bg-gray-700 rounded transition-colors"
                  title={sidebarOpen ? "Close sidebar" : "Open sidebar"}
                >
                  {sidebarOpen ? (
                    <X className="w-5 h-5 text-gray-400" />
                  ) : (
                    <Menu className="w-5 h-5 text-gray-400" />
                  )}
                </button>
                <Database className="w-8 h-8 text-blue-500" />
                <div>
                  <h1 className="text-2xl font-bold text-gray-100">
                    {/* {currentNotebook.name} */}
                  </h1>
                  <p className="text-sm text-gray-400">
                    Interactive SQL query environment
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Notebook Content */}
        <div className="flex-1 overflow-y-auto">
          <div className="max-w-6xl mx-auto px-6 py-6">
            {/* Connection Info */}
            {/* <div className="bg-blue-900 bg-opacity-30 border border-blue-700 rounded-lg p-4 mb-6">
              <div className="flex items-center gap-2 text-blue-300">
                <Database className="w-5 h-5" />
                <span className="font-medium">Connected to:</span>
                <span className="font-mono text-sm">
                  postgresql://localhost:5432/mydb
                </span>
              </div>
            </div> */}

            {/* Cells */}
            {cells.map((cell) => (
              <SQLCell
                key={cell.id}
                cell={cell}
                onUpdate={updateCell}
                onDelete={deleteCell}
                onExecute={executeCell}
              />
            ))}
            <button
              onClick={addCell}
              className="w-full py-3 border-2 border-dashed border-gray-700 rounded-lg hover:border-blue-500 hover:bg-gray-800 transition-colors flex items-center justify-center gap-2 text-gray-400 hover:text-blue-400"
            >
              <Plus className="w-5 h-5" />
              Add Cell
            </button>

            {/* Tips */}
            <div className="mt-6 bg-yellow-900 bg-opacity-20 border border-yellow-700 rounded-lg p-4">
              <h3 className="font-semibold text-yellow-300 mb-2">💡 Tips:</h3>
              <ul className="text-sm text-yellow-200 space-y-1">
                <li>• Press the Play button to execute a cell</li>
                <li>• Each cell maintains its execution order number</li>
                <li>• Results are displayed immediately below each cell</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotebookList;
